import { Box, FormHelperText, List, ListItem, Stack, Theme } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProps } from '../helpers';
import { createUseStyles } from 'react-jss';
import Text from '@/components/text';
import { getUiOptionLabel, UiOption } from '@/lib/helpers';
import Tooltip from '@/components/tooltip';
import TextInput from '../text-input';
import { debounce } from 'lodash';
import FlatButton from '@/components/flat-button';
import Chip from '@/components/chip';
import SelectMulti from '../select-multi';
import OnOffToggleButton from '../on-off-toggle-button';
import { getEntityValue, OnOffAction, OnOffEntity, updateEntities } from './helpers';
import { OnOffToggleButtonValue } from '../on-off-toggle-button/helpers';

const actionToToggleValue = (action: OnOffAction | undefined): OnOffToggleButtonValue => {
  if (action === 'allow') {
    return true;
  }
  if (action === 'block') {
    return false;
  }
  return undefined;
};

interface Props extends FormProps {
  options: UiOption[];
  value: OnOffEntity<string>[] | undefined;
  onChange: (values: OnOffEntity<string>[] | undefined) => void;
  idField: string;
  height?: string | number;
  search?: boolean;
  categoryOptions?: UiOption[];
  onSettings?: (option: UiOption) => void;
  defaultValue?: OnOffToggleButtonValue;
}

const useStyles = createUseStyles<string, { height: string | number }>((theme: Theme) => ({
  onOffList: ({ height }) => ({
    border: `1px solid ${theme.palette.gray}`,
    borderRadius: theme.shape.borderRadius,
    overflowY: 'auto',
    '& ul': {
      height: height || 'auto',
      overflowY: 'auto',
      '&.MuiList-root': {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    '& .MuiFormControl-root': {
      padding: 0,
      margin: 0,
    },
    '& li.MuiListItem-root': {
      padding: '0px 16px',
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
  }),
  header: {
    padding: '10px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: 10,
  },
  selectAllButton: {
    marginRight: 7,
  },
}));

const getOptionCategories = (meta?: Record<string, unknown>): string[] => {
  if (!Array.isArray(meta?.categories)) {
    return [];
  }
  return meta.categories as string[];
};

const OnOffList: FC<Props> = ({
  error,
  onChange,
  options,
  name,
  value,
  idField,
  disabled,
  readonly,
  height = '',
  emptyMessage = '',
  search = false,
  categoryOptions = [],
  onSettings,
  withNotSet = true,
  defaultValue,
}) => {
  const styles = useStyles({ height });
  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<UiOption[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllEnabled, setSelectAllEnabled] = useState(true);

  const filteredOptions = useMemo(() => {
    if (!filter && !categoryFilter?.length) {
      return options;
    }

    let filteredOptions = options;

    // Filter by categories
    const selectedCategories = new Set(categoryFilter.map(({ value }) => value));
    if (categoryFilter?.length) {
      filteredOptions = filteredOptions.filter(({ meta }) => {
        const optionCategories = getOptionCategories(meta);
        return optionCategories.some((category) => selectedCategories.has(category));
      });
    }

    // Filter by keyword
    if (filter) {
      const safeFilter = String(filter).toLowerCase().trim();
      filteredOptions = filteredOptions.filter((option) => {
        const { meta } = option;
        const safeLabel = getUiOptionLabel(option);
        const labelMatch = String(safeLabel).toLowerCase().includes(safeFilter);

        if (labelMatch) {
          return true;
        }

        const optionCategories = getOptionCategories(meta);
        const categoryLabelMatch = optionCategories.some((category) => {
          const safeCategory = String(category).toLowerCase();
          return safeCategory.includes(safeFilter);
        });

        return categoryLabelMatch;
      });
    }

    return filteredOptions;
  }, [options, filter, categoryFilter]);

  const handleSettings = (option: UiOption) => {
    onSettings?.(option);
  };

  const handleChange = (updatedEntities: OnOffEntity<string>[] | undefined) => {
    onChange(updatedEntities);
  };

  const handleSwitchChange = (optValue: string, toggleValue: OnOffToggleButtonValue) => {
    const action: OnOffAction | undefined =
      toggleValue === true ? 'allow' : toggleValue === false ? 'block' : undefined;
    const updatedEntities = updateEntities(value, idField, optValue, action);
    handleChange(updatedEntities.length ? updatedEntities : undefined);
  };

  const handleSelectAll = () => {
    const action: OnOffAction = selectAll ? 'block' : 'allow';
    const existingEntities = value ?? [];
    const filteredOptionValues = new Set(
      filteredOptions.filter((opt) => !opt.disabled).map((opt) => String(opt.value))
    );

    const preservedEntities = existingEntities.filter(
      (entity) => !filteredOptionValues.has(String(entity[idField]))
    );

    let updatedEntities = [...preservedEntities];

    filteredOptions.forEach(({ disabled, value }) => {
      if (disabled || value == null) {
        return;
      }
      updatedEntities = updateEntities(updatedEntities, idField, value, action);
    });

    handleChange(updatedEntities.length ? updatedEntities : undefined);
  };

  const handleUnsetAll = () => {
    handleChange(undefined);
  };

  const handleChangeKeyword = (value: string) => {
    setKeyword(value);
    debounceHandleChangeFilter(value);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceHandleChangeFilter = useCallback(
    debounce((keyword: string) => {
      setFilter(keyword);
    }, 400),
    []
  );

  const handleCategorySelectChange = (selectedCategories: UiOption[]) => {
    setCategoryFilter(selectedCategories);
  };

  useEffect(() => {
    if (!filteredOptions.length) {
      setSelectAllEnabled(false);
      return;
    }
    setSelectAllEnabled(true);
    setSelectAll(
      filteredOptions.every(
        ({ value: optValue, disabled: optDisabled }) =>
          optDisabled || getEntityValue(value, optValue, idField) === 'allow'
      )
    );
  }, [filteredOptions, value, idField]);

  const hasError = !!error;
  const hasItems = !!filteredOptions?.length;
  const hasFilter = !!filter;
  const hasCategories = !!categoryOptions.length;

  return (
    <Stack className={styles.onOffList}>
      <Stack
        direction="row"
        className={styles.header}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" gap={1}>
          <Box width="200px">
            {search && (
              <TextInput
                name="keyword"
                onChange={handleChangeKeyword}
                value={keyword}
                size="x-small"
                placeholder="Filter by name"
                clearable
                fullWidth
                disabled={disabled || readonly}
              />
            )}
          </Box>
          {hasCategories && (
            <Box width="300px">
              <SelectMulti
                name="select-multi-filter"
                options={categoryOptions || []}
                value={categoryFilter.map(({ value }) => value)}
                onChange={handleCategorySelectChange}
                size="small"
                label="Filter by category"
                light
                disabled={disabled || readonly}
              />
            </Box>
          )}
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          <FlatButton
            label={selectAll ? 'Disable All' : 'Enable All'}
            onClick={handleSelectAll}
            disabled={disabled || readonly || !selectAllEnabled}
            size="small"
            color="link"
          />
          {withNotSet && (
            <>
              <Box>&middot;</Box>
              <FlatButton
                label="Inherit All"
                onClick={handleUnsetAll}
                disabled={disabled || readonly || !value?.length}
                size="small"
                color="link"
              />
            </>
          )}
        </Stack>
      </Stack>

      <List>
        {!hasItems && (
          <ListItem>
            <Box my={2}>
              <Text italic color="grey">
                {hasFilter ? 'No matches found' : emptyMessage || 'No options available'}
              </Text>
            </Box>
          </ListItem>
        )}
        {filteredOptions.map((option, index) => {
          const { label, value: optValue, disabled: optDisabled, tooltip, meta } = option;
          const entityAction = getEntityValue(value, optValue, idField);
          const rawToggleValue = actionToToggleValue(entityAction);
          const toggleValue =
            rawToggleValue === undefined && defaultValue !== undefined
              ? defaultValue
              : rawToggleValue;

          const { hasSettings = false, categories = [] } =
            (meta as { hasSettings: boolean; categories: string[] }) || {};
          const hasCategories = !!categories?.length;

          let ButtonEl = (
            <OnOffToggleButton
              name={name}
              value={toggleValue}
              onChange={(newValue) => handleSwitchChange(optValue, newValue)}
              disabled={optDisabled || disabled || readonly}
              fullWidth={false}
              withNotSet={withNotSet}
            />
          );

          if (tooltip) {
            ButtonEl = <Tooltip title={tooltip}>{ButtonEl}</Tooltip>;
          }

          return (
            <ListItem key={index}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                py={2}
              >
                <Stack direction="column" gap={1} width="100%">
                  <Box flexGrow={1}>{label}</Box>
                  {hasCategories && (
                    <Stack direction="row" gap={1}>
                      {categories.map((category) => (
                        <Chip key={category} label={category} size="x-small" />
                      ))}
                    </Stack>
                  )}
                </Stack>
                <Stack gap={2} direction="row" alignItems="center">
                  {hasSettings && (
                    <FlatButton
                      label="Settings"
                      icon="settings"
                      size="small"
                      onClick={() => handleSettings(option)}
                      disabled={entityAction !== 'allow'}
                    />
                  )}
                  <Box>{ButtonEl}</Box>
                </Stack>
              </Stack>
            </ListItem>
          );
        })}
      </List>
      {hasError && <FormHelperText error>{error}</FormHelperText>}
    </Stack>
  );
};

export default OnOffList;
